// services/school-ui/src/pages/promotion/DecisionFooter.tsx
// Pinned-bottom decision controls. Owns the approve + reject mutations.
// Both approve and reject are single-click by default. The "Add note"
// expander reveals a textarea + two submit buttons so the admin can
// attach an optional reason to either direction. Rejection reasons
// used to be required; Charles asked for them to be optional so
// quick rejects don't need an extra click.

import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ConflictError, decide } from "./api";
import type { PromotionRequest } from "./types";

interface Props {
  request: PromotionRequest;
  onDecided: (action: "approve" | "reject", notes: string) => void;
  onError: (message: string) => void;
}

export function DecisionFooter({ request, onDecided, onError }: Props): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Single "note" mode now: when the admin clicks "Add note" the
  // textarea opens and they pick approve / reject from there. No
  // separate "reject" mode — quick rejects skip the form entirely.
  const [mode, setMode] = useState<"note" | "reject" | null>(null);
  const [notes, setNotes] = useState("");
  // §5.5 终审改目标:默认原目标;可切 default(全校)或其他学科库
  const [targetOverride, setTargetOverride] = useState("");

  const mutation = useMutation({
    mutationFn: ({ action, notes }: { action: "approve" | "reject"; notes: string }) =>
      decide(request.id, action, notes || undefined,
        action === "approve" && targetOverride ? targetOverride : undefined),
    onSuccess: (_data, vars) => {
      setMode(null);
      setNotes("");
      void queryClient.invalidateQueries({ queryKey: ["admin-promotions"] });
      void queryClient.invalidateQueries({ queryKey: ["admin-promotions-pending-count"] });
      onDecided(vars.action, vars.notes);
    },
    onError: (e) => {
      if (e instanceof ConflictError) {
        onError(t("promotion.decision.alreadyDecided", { reviewer: "another admin" }));
      } else {
        onError((e as Error).message);
      }
    },
  });

  if (request.status !== "pending") {
    return (
      <div className="border-t border-slate-200 px-6 py-3 text-xs text-slate-500">
        {request.decision_notes && (
          <span>
            <em>note:</em> {request.decision_notes}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="border-t border-slate-200 px-6 py-3">
      {mode === null && (
        <div className="flex items-center gap-2">
          <button
            onClick={() => mutation.mutate({ action: "approve", notes: "" })}
            disabled={mutation.isPending}
            className="text-sm px-4 py-1.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-600 disabled:opacity-50"
          >
            {mutation.isPending && mutation.variables?.action === "approve"
              ? t("promotion.decision.saving")
              : t("promotion.decision.approve")}
          </button>
          <button
            onClick={() => {
              // §5.5: 拒绝必填理由 — 打开理由态而不是直接拒
              setMode("reject");
              setNotes("");
            }}
            disabled={mutation.isPending}
            className="text-sm px-4 py-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
          >
            {t("promotion.decision.reject")}
          </button>
          <select
            value={targetOverride}
            onChange={(e) => setTargetOverride(e.target.value)}
            className="text-xs px-2 py-1 rounded border border-slate-200 text-slate-600"
            title={t("promotion.decision.targetOverrideTip")}
          >
            <option value="">{t("promotion.decision.targetKeep")}</option>
            <option value="default">{t("promotion.decision.targetDefault")}</option>
          </select>
          <button
            onClick={() => {
              setMode("note");
              setNotes("");
            }}
            className="ml-auto text-xs text-slate-500 hover:underline"
          >
            {t("promotion.decision.addNote")}
          </button>
        </div>
      )}

      {mode === "reject" && (
        <div className="space-y-2">
          <textarea
            autoFocus
            placeholder={t("promotion.decision.rejectReasonPlaceholder")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-2 py-1 text-sm rounded bg-red-50 border border-red-200"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => mutation.mutate({ action: "reject", notes })}
              disabled={mutation.isPending || !notes.trim()}
              className="text-xs px-3 py-1 rounded bg-red-100 text-red-600 disabled:opacity-50"
            >
              {t("promotion.decision.confirmReject")}
            </button>
            <button
              type="button"
              onClick={() => setMode(null)}
              className="text-xs px-3 py-1 rounded hover:bg-slate-100 text-slate-500"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}

      {mode === "note" && (
        <div className="space-y-2">
          <textarea
            autoFocus
            placeholder={t("promotion.decision.notesPlaceholderShared")}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full px-2 py-1 text-sm rounded bg-slate-100 border border-slate-200"
            rows={2}
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => mutation.mutate({ action: "approve", notes })}
              disabled={mutation.isPending}
              className="text-xs px-3 py-1 rounded bg-blue-100 text-blue-600 disabled:opacity-50"
            >
              {mutation.isPending && mutation.variables?.action === "approve"
                ? t("promotion.decision.saving")
                : t("promotion.decision.approveWithNote")}
            </button>
            <button
              type="button"
              onClick={() => mutation.mutate({ action: "reject", notes })}
              disabled={mutation.isPending}
              className="text-xs px-3 py-1 rounded bg-red-100 text-red-600 disabled:opacity-50"
            >
              {mutation.isPending && mutation.variables?.action === "reject"
                ? t("promotion.decision.saving")
                : t("promotion.decision.rejectWithNote")}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode(null);
                setNotes("");
              }}
              className="text-xs px-3 py-1 rounded hover:bg-slate-100"
            >
              {t("promotion.decision.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
